import { useAuth } from '@renderer/chat/providers'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, UseMutationResult } from '@tanstack/react-query'
import { v1Client } from '@renderer/chat'
import { PAGINATION_LIMIT } from '@renderer/utils/constants'
import { useMemo, useCallback } from 'react'
import { AudioNote } from '@highlight/common'
import { useGlobalStore } from '@highlight/ipc/react'

export const SHARED_ITEMS_QUERY_KEY = 'shared-items'
export const PENDING_INVITATIONS_QUERY_KEY = 'pending-invitations'
const VIEWED_ITEMS_KEY = 'shared-items-viewed'

type SharedAudioNote = {
  type: 'audio_note'
  id: string
  sharedBy: {
    name: string
    email: string
    imageUrl?: string
  }
  sharedAt: string
  isShared: true
  content: AudioNote
}

type PendingInvitation = {
  id: string
  type: 'audio_note'
  itemId: string
  title: string
  sharedBy: {
    name: string
    email: string
    imageUrl?: string
  }
  sharedAt: string
  message?: string
  meeting?: {
    app: {
      name: string
    }
    windowTitle?: string
    url?: string
    attendees?: Array<{
      name: string
      email: string
    }>
  }
}

type SharedItemsResponse = {
  data: Array<{
    type: 'audio_note'
    id: string
    sharedBy: {
      name: string
      email: string
      imageUrl?: string
    }
    sharedAt: string
    isShared: true
    content: {
      id: string
      title?: string
      summary?: string
      transcript?: string
      startedAt: string | Date
      endedAt: string | Date
      createdAt?: string | Date
      meeting?: unknown
      shareLink?: string
      userId?: string
    }
  }>
  nextCursor?: string
  hasNextPage: boolean
}

type PendingInvitationsResponse = {
  data: PendingInvitation[]
}

const PENDING_INVITATIONS_REFETCH_INTERVAL = 30000 // 30 seconds

function useSharedItems() {
  const { getAuthToken } = useAuth()

  const query = useInfiniteQuery({
    queryKey: [SHARED_ITEMS_QUERY_KEY],
    queryFn: async ({ pageParam }: { pageParam: string }) => {
      const response = await v1Client.GET('/shares', {
        params: {
          query: {
            type: 'audio_note',
            limit: PAGINATION_LIMIT,
            cursor: pageParam || undefined,
          },
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Failed to fetch shared items')
      }

      const data = (response.data as SharedItemsResponse) ?? { data: [], nextCursor: undefined, hasNextPage: false }

      // Map shared items to unified format with proper type
      const mappedData = (data.data ?? []).map((item) => {
        if (item.type === 'audio_note' && item.content) {
          const audioNote = item.content
          const mappedItem = {
            ...item,
            content: {
              id: audioNote.id,
              title: audioNote.title || '',
              summary: audioNote.summary || '',
              transcript: audioNote.transcript || '',
              startedAt: new Date(audioNote.startedAt ?? Date.now()),
              endedAt: new Date(audioNote.endedAt ?? Date.now()),
              meeting: audioNote.meeting,
              shareLink: audioNote.shareLink,
              userId: audioNote.userId || '',
            } as AudioNote,
          } as SharedAudioNote
          return mappedItem
        }
        return item
      })

      return {
        data: mappedData,
        nextCursor: data.nextCursor,
      }
    },
    initialPageParam: '',
    getNextPageParam: (lastPage: { nextCursor?: string }) => lastPage.nextCursor,
    staleTime: 60000, // 1 minute
  })

  const sharedItems = useMemo(() => {
    return query.data?.pages.flatMap((page) => (Array.isArray(page?.data) ? page.data : [])) ?? []
  }, [query.data])

  return useMemo(() => {
    return {
      ...query,
      data: sharedItems,
    }
  }, [sharedItems, query])
}

function usePendingInvitations() {
  const { getAuthToken } = useAuth()

  return useQuery({
    queryKey: [PENDING_INVITATIONS_QUERY_KEY],
    queryFn: async () => {
      const response = await v1Client.GET('/shares/pending', {
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Failed to fetch pending invitations')
      }

      return (response.data as PendingInvitationsResponse)?.data ?? []
    },
    staleTime: 0,
    refetchInterval: PENDING_INVITATIONS_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
  })
}

function useRespondToInvitation(): UseMutationResult<
  unknown,
  Error,
  { invitationId: string; action: 'accept' | 'decline' },
  unknown
> {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['respond-to-invitation'],
    mutationFn: async ({ invitationId, action }: { invitationId: string; action: 'accept' | 'decline' }) => {
      const response = await v1Client.POST('/shares/pending/{id}/respond', {
        params: {
          path: {
            id: invitationId,
          },
        },
        body: {
          action,
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Failed to respond to invitation')
      }

      return response.data
    },
    onSuccess: (_, variables) => {
      // Invalidate both pending and shared items queries
      queryClient.invalidateQueries({ queryKey: [PENDING_INVITATIONS_QUERY_KEY] })
      if (variables.action === 'accept') {
        queryClient.invalidateQueries({ queryKey: [SHARED_ITEMS_QUERY_KEY] })
      }
    },
    onError: (error) => {
      console.error('Failed to respond to invitation:', error)
    },
  })
}

function useMarkSharedItemAsViewed() {
  const [viewedItems, setViewedItems] = useGlobalStore<string[]>('user', VIEWED_ITEMS_KEY, [])

  const markAsViewed = useCallback(
    (itemId: string) => {
      setViewedItems((prev: string[] | undefined) => {
        const currentItems = prev || []
        if (!currentItems.includes(itemId)) {
          return [...currentItems, itemId]
        }
        return currentItems
      })
    },
    [setViewedItems],
  )

  return { markAsViewed, viewedItems }
}

export { useSharedItems, usePendingInvitations, useRespondToInvitation, useMarkSharedItemAsViewed }
export type { SharedAudioNote, PendingInvitation }
